// the show slot (Oct 27, programming-plan.md Phase D; config/clock.json `shows`): at :15 and :45 by day, a short show
// of screens in a set order behind a plate with its name. The Morning Commute weekdays 6:00 to 9:30, the Weekend
// Planner from Friday 4 PM through Sunday, Around SF otherwise. A show's screens play whatever the loop rules say;
// a camera item runs the Views block on a theme for a set time. The rotation (rotation.mjs) plays the queue ahead of
// the spine, so when the show runs out the loop carries on where it was.
import { showDisplay } from '../navigation.mjs';
import STATUS from '../status.mjs';
import { playBumper } from './bumper.mjs';
import { daypart } from './music.mjs';
import { byHand } from './snapshot-display.mjs';
import { enqueue, takeNext, find } from './rotation.mjs';
import { data } from './snapshot.mjs';
import sfoTickerLine from './sfo.mjs';
import crabDays from './crab.mjs';

const { clock } = window.RHL;
const shows = clock.shows ?? {};

const local = (now) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
		timeZone: clock.timeZone, hour: 'numeric', minute: 'numeric', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(now).map((p) => [p.type, p.value]));
	return { day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday), at: Number(parts.hour) * 60 + Number(parts.minute) };
};

// which show the slot holds at this moment
const showAt = (now = new Date()) => {
	const { day, at } = local(now);
	const commute = shows['morning-commute'];
	if (commute && day >= 1 && day <= 5 && at >= commute.from[0] * 60 + commute.from[1] && at < commute.to[0] * 60 + commute.to[1]) return 'morning-commute';
	if (shows['weekend-planner'] && (day === 0 || day === 6 || (day === 5 && at >= 16 * 60))) return 'weekend-planner';
	return shows['around-sf'] ? 'around-sf' : null;
};
const titleAt = (now) => shows[showAt(now)]?.title ?? ['The', 'Show'];

const has = (display) => !!display && byHand(() => display.status === STATUS.loaded) && (display.hasData?.() ?? true);
const conditions = {
	ready: (display) => has(display),
	rules: (display) => has(display) && (display.airsNow?.(new Date()) ?? true),
	sfo: (display) => has(display) && (!!sfoTickerLine() || !!(data('faa') ?? []).find((a) => a.airport === 'SFO')?.departures),
	crab: (display) => has(display) && crabDays({ tides: data('tides'), swell: data('swell'), wind: data('wind') })
		.some((d) => d.score >= (clock.loopRules?.crabGoodScore ?? 6) && new Date(d.high.t) - Date.now() < 3 * 86400000),
};

// the screens for a show now: element ids, or the Views block armed on a theme for a set time
const itemsFor = (key) => (shows[key]?.items ?? []).map((item) => {
	const display = find(item.id);
	if (!display) return null;
	if (item.if && !conditions[item.if]?.(display)) return null;
	if (!item.if && !has(display)) return null;
	if (item.seconds) {
		return {
			display,
			before: () => display.arm({ scheduled: false, options: { seconds: item.seconds, theme: item.theme } }),
		};
	}
	return item.id;
}).filter(Boolean);

// run the show now: its plate, then its screens; false when there's nothing to show
const runShow = (now = new Date()) => {
	if (daypart(now).name === 'night') return false;
	const key = showAt(now);
	const items = itemsFor(key);
	if (!items.length) return false;
	enqueue(items);
	const first = takeNext();
	// a voiced intro gets the bell (none is recorded yet, so for now the plate drops in silence)
	const line = clock.voice.featureIntros?.[key]?.[0] ?? null;
	playBumper({
		title: shows[key].title, line, sting: line && clock.bells?.featureIntros === true ? 'bell-2' : null, upNext: first?.name, onCovered: () => first && byHand(() => showDisplay(first)),
	});
	return true;
};

window.RHL.showAt = (date) => showAt(date ? new Date(date) : new Date());

export {
	runShow, showAt, titleAt,
};
